/*
 * Brand-filter de-duplicator.
 *
 * The vendor field gets the same brand entered in different short forms on the
 * stock system (e.g. "B YOUNG" vs "b.young"), so the vendor-based brand filter
 * lists the brand more than once. This merges rows whose names normalise to the
 * same thing into a single row:
 *   - counts are summed,
 *   - the filter link applies EVERY variant vendor value at once
 *     (filter.p.vendor=b.young&filter.p.vendor=B YOUNG — Shopify OR-combines
 *     them), so clicking it still returns the whole brand,
 *   - the label is the collection title (window.__ssBrandNames), falling back to
 *     the nicest spelling present, else Title Case.
 *
 * Auto — no maintained list; a new short-name variant merges itself the moment
 * it appears. Runs on the collection/search sidebar brand filter AND the header
 * search dropdown's BRANDS column, and re-runs when either re-renders. Purely a
 * display enhancement: if it errors, the original rows just show unchanged.
 */
(function () {
  if (window.__ssBrandDedupInit) return;
  window.__ssBrandDedupInit = true;

  var names = window.__ssBrandNames || {};

  // Manual brand aliases — for vendor spellings that are the same brand but DON'T
  // normalise to the same key (genuinely different wording, not just casing or
  // punctuation). Auto-merge already handles "b.young"/"B YOUNG" etc; this list
  // is only for cases like "Masai" + "Masai Clothing". Every key mapping to the
  // same value merges into one filter row shown under that value. Add new pairs
  // here as they come up (key = any vendor spelling, value = the display name).
  var BRAND_ALIASES = {
    'MASAI': 'Masai',
    'Masai Clothing': 'Masai',
    'POSTCARD': 'A Postcard from Brighton',
    'A Postcard from Brighton': 'A Postcard from Brighton'
  };

  function norm(s) {
    return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // Normalised-key → display name, derived from BRAND_ALIASES.
  var aliasByNorm = {};
  Object.keys(BRAND_ALIASES).forEach(function (k) {
    aliasByNorm[norm(k)] = BRAND_ALIASES[k];
  });
  // The group key a label belongs to: its alias's normalised name if aliased,
  // else its own normalised key. So aliased spellings collapse into one group.
  function groupKeyOf(label) {
    var k = norm(label);
    return aliasByNorm[k] ? norm(aliasByNorm[k]) : k;
  }
  function titleCase(s) {
    return s.toLowerCase().replace(/\b\w/g, function (m) { return m.toUpperCase(); });
  }
  function labelOf(a) {
    return a.textContent.replace(/\s*\(\s*[\d,]+\s*\)\s*$/, '').trim();
  }
  function countOf(a) {
    var m = a.textContent.match(/\(\s*([\d,]+)\s*\)/);
    return m ? parseInt(m[1].replace(/,/g, ''), 10) : null;
  }
  function vendorsOf(href) {
    try {
      return new URL(href, location.origin).searchParams.getAll('filter.p.vendor');
    } catch (e) {
      return [];
    }
  }

  function currentVendors() {
    try {
      return new URLSearchParams(location.search).getAll('filter.p.vendor');
    } catch (e) {
      return [];
    }
  }

  function sameVendorSet(a, b) {
    if (a.length !== b.length) return false;
    a = a.slice().sort();
    b = b.slice().sort();
    for (var i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  // Is this a genuine BRAND-filter row (vs a size/colour/pattern/type row that
  // merely CARRIES the active vendor param)? When a vendor filter is applied,
  // every facet row's href includes filter.p.vendor= to preserve it — so the
  // raw attribute selector alone would wrongly treat size/colour values as
  // brands. A brand row is one that CHANGES the vendor set (adds/removes a
  // vendor); other facets carry it unchanged.
  function isBrandRow(a) {
    var href = a.getAttribute('href') || '';
    var isSidebar = /ss-csb__link/.test(a.className || '');
    if (href.indexOf('filter.p.vendor=') === -1) {
      // No vendor param: only a sidebar brand row whose active (un-tick) link
      // dropped the last vendor to a bare path qualifies.
      return isSidebar && /--active/.test(a.className || '') && currentVendors().length > 0;
    }
    if (!isSidebar) return true; // header-search dropdown brand row — always dedupe
    return !sameVendorSet(vendorsOf(href), currentVendors());
  }

  // Relabel a row's brand name IN PLACE, preserving its structural children
  // (checkbox span, count span). We only ever touch the visible name text and,
  // for merged rows, the count total — never rebuild the anchor. This keeps the
  // `.ss-csb__label` (flex:1) span intact so the count stays right-aligned, and
  // preserves the checkbox + its checked state.
  function relabel(a, display, countTotal) {
    var labelEl =
      a.querySelector('.ss-csb__label') ||
      a.querySelector('[class*="__label"]') ||
      a.querySelector('[class*="label"]');
    var countEl = a.querySelector('[class*="count"]');

    if (labelEl) {
      // Sidebar markup: dedicated label span — just set its text.
      labelEl.textContent = display;
    } else {
      // Header-dropdown markup: the name is a bare leading text node followed by
      // the count span. Replace the text node(s), keep the count span in place.
      Array.prototype.slice.call(a.childNodes).forEach(function (n) {
        if (n.nodeType === 3) a.removeChild(n);
      });
      a.insertBefore(
        document.createTextNode(display + (countEl ? ' ' : '')),
        a.firstChild
      );
    }

    if (countTotal != null && countEl) {
      countEl.textContent = '(' + countTotal + ')';
    }
  }

  function dedupeList(list) {
    if (!list || list.__ssBrandDone) return;
    list.__ssBrandDone = true;

    // Only genuine brand rows — excludes size/colour/pattern rows that merely
    // carry the active vendor param, and includes active brand rows whose
    // remove-link has no vendor param.
    var links = Array.prototype.slice.call(list.querySelectorAll('a')).filter(isBrandRow);
    if (!links.length) return;

    var groups = {};
    links.forEach(function (a) {
      var key = groupKeyOf(labelOf(a));
      if (!key) return;
      (groups[key] = groups[key] || []).push(a);
    });

    var currentVendors = [];
    try { currentVendors = new URLSearchParams(location.search).getAll('filter.p.vendor'); } catch (e) {}

    Object.keys(groups).forEach(function (key) {
      var grp = groups[key];

      // Display name for EVERY brand row — merged or standalone. Prefer a manual
      // alias name, then the collection-title map, then the nicest spelling
      // present, else Title Case.
      var labels = grp.map(labelOf);
      var aliasDisplay = null;
      grp.forEach(function (a) {
        var al = aliasByNorm[norm(labelOf(a))];
        if (al) aliasDisplay = al;
      });
      var nice = labels.filter(function (l) { return /[a-z]/.test(l); })[0];
      var display = aliasDisplay || names[key] || nice || titleCase(labels[0]);

      // Standalone brand (no duplicate spelling): relabel only, leave the link
      // and count untouched.
      if (grp.length < 2) {
        relabel(grp[0], display, null);
        return;
      }

      // Merged brand: collect all variant vendors + sum counts.
      var vendors = [];
      var total = 0;
      var haveCount = false;
      grp.forEach(function (a) {
        // Use the row's own LABEL as its vendor value — never vendorsOf(href).
        // When another brand is already filtered, every row's href carries that
        // active vendor too, which would wrongly mark this brand active and
        // pollute the merged link. The label is the row's true vendor value.
        var lbl = labelOf(a);
        if (lbl && vendors.indexOf(lbl) === -1) vendors.push(lbl);
        var c = countOf(a);
        if (c != null) { total += c; haveCount = true; }
      });

      var keeper = grp[0];
      var isActive = vendors.some(function (v) { return currentVendors.indexOf(v) !== -1; });

      // Build the keeper's link from the keeper's OWN href — NOT from location.
      // The row already points at the right place (the collection URL for the
      // sidebar, or /search?q=… for the header dropdown); we only swap in this
      // brand's full vendor set. Basing it on location wrongly rewrote dropdown
      // links to the current page, losing the /search?q= term (so a merged brand
      // returned the wrong products). Strip this brand's spellings, keep any
      // other vendors already on the link, then re-add all spellings unless the
      // brand is already active (in which case the link un-ticks it).
      try {
        var base = keeper.getAttribute('href') || (location.pathname + location.search);
        var u = new URL(base, location.origin);
        var others = u.searchParams.getAll('filter.p.vendor').filter(function (v) {
          return vendors.indexOf(v) === -1;
        });
        u.searchParams.delete('filter.p.vendor');
        others.forEach(function (v) { u.searchParams.append('filter.p.vendor', v); });
        if (!isActive) {
          vendors.forEach(function (v) { u.searchParams.append('filter.p.vendor', v); });
        }
        keeper.setAttribute('href', u.pathname + (u.search || '') + u.hash);
      } catch (e) { /* leave href as-is */ }

      relabel(keeper, display, haveCount ? total : null);

      // Keep the merged row checked if the brand is active (URL or server-marked).
      var anyActive = isActive || grp.some(function (a) { return /--active/.test(a.className || ''); });
      if (anyActive && !/--active/.test(keeper.className || '')) {
        keeper.className += ' ss-csb__link--active';
      }

      // Hide the now-merged duplicates.
      grp.slice(1).forEach(function (a) {
        var li = a.closest('li');
        (li || a).style.display = 'none';
      });
    });
  }

  // Merge + relabel the "Your Selection" brand chips. A merged brand applies
  // several vendor spellings, so the server renders one removable chip PER
  // spelling (e.g. "Brand: POSTCARD" AND "Brand: A Postcard from Brighton") —
  // looks duplicated and cramped. Collapse each brand's chips into ONE chip that
  // reads the proper name and whose × removes ALL of that brand's spellings.
  // Runs per "Your Selection" container (there can be a desktop + a mobile one).
  function mergeBrandChips() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.ss-csb__body--selection'),
      function (container) {
        var groups = {};
        Array.prototype.forEach.call(container.querySelectorAll('.ss-csb__chip'), function (chip) {
          var span = chip.querySelector('span');
          if (!span) return;
          var m = span.textContent.trim().match(/^([^:]+):\s*(.+)$/);
          if (!m) return;
          var prefix = m[1].trim();
          var value = m[2].trim();
          if (!/^(brand|vendor)$/i.test(prefix)) return; // brand chips only
          var key = groupKeyOf(value);
          (groups[key] = groups[key] || []).push({ chip: chip, span: span, prefix: prefix, value: value });
        });

        Object.keys(groups).forEach(function (key) {
          var grp = groups[key];
          var aliasDisp = null;
          grp.forEach(function (g) { var al = aliasByNorm[norm(g.value)]; if (al) aliasDisp = al; });
          var nice = grp.map(function (g) { return g.value; })
            .filter(function (l) { return /[a-z]/.test(l); })[0];
          var display = aliasDisp || names[key] || nice || titleCase(grp[0].value);

          var keeper = grp[0];
          keeper.span.textContent = keeper.prefix + ': ' + display;

          if (grp.length > 1) {
            // Rebuild keeper's × link to strip EVERY spelling of this brand.
            var groupVendors = grp.map(function (g) { return g.value; });
            try {
              var u = new URL(location.pathname + location.search, location.origin);
              var remaining = u.searchParams.getAll('filter.p.vendor').filter(function (v) {
                return groupVendors.indexOf(v) === -1;
              });
              u.searchParams.delete('filter.p.vendor');
              remaining.forEach(function (v) { u.searchParams.append('filter.p.vendor', v); });
              keeper.chip.setAttribute('href', u.pathname + (u.search || '') + u.hash);
            } catch (e) { /* leave href as-is */ }
            grp.slice(1).forEach(function (g) { g.chip.style.display = 'none'; });
          }
        });
      }
    );
  }

  function run() {
    // Candidate anchors: sidebar links + any vendor-param links (dropdown). Only
    // those that are genuine brand rows drive list selection, so the size/colour
    // lists (which carry the vendor param when a brand is active) are ignored.
    var links = document.querySelectorAll('a.ss-csb__link, a[href*="filter.p.vendor="]');
    var seen = [];
    Array.prototype.forEach.call(links, function (a) {
      if (!isBrandRow(a)) return;
      var li = a.closest('li');
      var list = li ? li.parentElement : a.parentElement;
      if (list && seen.indexOf(list) === -1) {
        seen.push(list);
        dedupeList(list);
      }
    });
    mergeBrandChips();
  }

  run();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  }

  // Re-run when the sidebar (results-list morph) or the search dropdown re-renders.
  // Lists are replaced on re-render, so their __ssBrandDone flag is gone and they
  // reprocess once; already-processed lists are skipped, so no loop.
  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      run();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
